import { Box, Stack, Typography } from "@mui/material";

import { Disclose, Tooltip } from "@lopie/kernel-ui";

/**
 * When it was allowed to run, and when it actually did.
 *
 * ## Why this shape exists and a run chart does not
 *
 * Every other product that draws agent runs draws the runs. It cannot draw
 * anything else, because it holds no statement of intent — the schedule lives
 * in a cron file, or a deployment, or somebody's head. This kernel holds both:
 * the cadence and window somebody declared, and the runs that happened. The
 * interesting thing is the *disagreement*, and it is only visible when both
 * are on one axis.
 *
 * ## The bands are the frame and the marks are the data
 *
 * The rule the kit states, applied literally. The declared window is drawn as
 * a band across each day and carries the colour; a run is a mark and stays
 * neutral. A run outside every band is the one thing worth seeing and it is
 * visible as position rather than as a badge somebody has to read.
 *
 * ## What it refuses to draw
 *
 * **Expected run times.** Given a cadence it is tempting to compute when the
 * ticker *should* have fired and draw ghosts for the ones that did not. That
 * would be reconstructing a scheduler's decisions from the outside and
 * presenting the reconstruction as a record — the same overstatement this
 * product spends every screen avoiding. The band is what was declared, which
 * is a fact; a tick inside it is not.
 *
 * **A window it did not have at the time.** The band is the window as
 * declared *now*, drawn back across days when a different window, or none,
 * may have been in force. A run sitting outside a band therefore means "this
 * would be outside the window you have set today", which is worth knowing and
 * is not the same sentence as "it broke the rules". The caption says so,
 * because the reader cannot tell from the picture.
 */

const DAY = 86400000;

/** `"09:00"` → minutes from midnight, or null. */
function minutes(clock) {
  const m = /^(\d{1,2}):(\d{2})$/.exec(String(clock || "").trim());
  if (!m) return null;
  const h = Number(m[1]);
  const mi = Number(m[2]);
  if (h > 23 || mi > 59) return null;
  return h * 60 + mi;
}

/**
 * What fraction of a day a run sits at, in the schedule's own timezone.
 *
 * Read in the declared zone rather than the viewer's, because the window is
 * declared in that zone: a run drawn at the reader's local hour against a band
 * drawn at the agent's would disagree by the offset between them, silently,
 * and look like a violation.
 */
function dayFraction(iso, zone) {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return null;
  try {
    const parts = new Intl.DateTimeFormat("en-GB", {
      timeZone: zone || "UTC",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    }).formatToParts(at);
    const h = Number(parts.find((p) => p.type === "hour")?.value);
    const mi = Number(parts.find((p) => p.type === "minute")?.value);
    if (Number.isNaN(h) || Number.isNaN(mi)) return null;
    return (h * 60 + mi) / 1440;
  } catch {
    // An unresolvable zone is the kernel's problem to refuse, not this
    // component's to guess at. UTC is the honest fallback and the caption
    // names the zone either way.
    return (at.getUTCHours() * 60 + at.getUTCMinutes()) / 1440;
  }
}

/** Which calendar day a run falls on, in the schedule's zone. */
function dayKey(iso, zone) {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return null;
  try {
    return new Intl.DateTimeFormat("en-CA", {
      timeZone: zone || "UTC",
      year: "numeric", month: "2-digit", day: "2-digit",
    }).format(at);
  } catch {
    return at.toISOString().slice(0, 10);
  }
}

export default function Kept({ agent, days = 7 }) {
  const schedule = agent?.schedule || null;
  const zone = schedule?.timezone || "UTC";

  // Every run this kernel has a time for. The series is the real source; the
  // last-run row is included because the series only began when per-run rows
  // did, and dropping the one run older than that would draw a blank strip
  // for an agent that has demonstrably run.
  const runs = [];
  for (const row of agent?.runs_series || []) {
    if (row.at) runs.push({ at: row.at, usd: row.usd, known: true });
  }
  const last = agent?.state?.last_run_at || agent?.spend?.last_run_at;
  if (last && !runs.some((r) => r.at === last)) {
    runs.push({ at: String(last).replace(" ", "T"), usd: agent?.spend?.last_run_usd, known: false });
  }

  const start = minutes(schedule?.window_start);
  const end = minutes(schedule?.window_end);
  const windowed = start !== null && end !== null;
  const wraps = windowed && start > end;

  const today = new Date();
  const columns = [];
  for (let i = days - 1; i >= 0; i -= 1) {
    const at = new Date(today.getTime() - i * DAY);
    const key = dayKey(at.toISOString(), zone);
    columns.push({
      key,
      label: new Intl.DateTimeFormat(undefined, {
        timeZone: zone, weekday: "short",
      }).format(at),
      runs: runs.filter((r) => dayKey(r.at, zone) === key),
    });
  }

  const outside = windowed
    ? runs.filter((r) => {
        const f = dayFraction(r.at, zone);
        if (f === null) return false;
        const at = f * 1440;
        return wraps ? at < start && at > end : at < start || at > end;
      })
    : [];

  const bands = !windowed
    ? [{ from: 0, to: 100 }]
    : wraps
      ? [{ from: 0, to: (end / 1440) * 100 },
         { from: (start / 1440) * 100, to: 100 }]
      : [{ from: (start / 1440) * 100, to: (end / 1440) * 100 }];

  return (
    <Box sx={{ minWidth: 0 }}>
      <Stack spacing={0.5}>
        {columns.map((col) => (
          <Stack
            key={col.key}
            direction="row"
            spacing={1}
            sx={{ alignItems: "center", minWidth: 0 }}
          >
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ width: 34, flex: "none" }}
            >
              {col.label}
            </Typography>
            <Box
              sx={{
                position: "relative",
                flex: 1,
                height: 14,
                borderRadius: 0.5,
                backgroundColor: "action.hover",
                minWidth: 0,
              }}
            >
              {/* The frame: when it was allowed to start. */}
              {bands.map((b, i) => (
                <Box
                  key={i}
                  sx={{
                    position: "absolute",
                    left: `${b.from}%`,
                    width: `${Math.max(b.to - b.from, 0.5)}%`,
                    top: 0,
                    bottom: 0,
                    backgroundColor: windowed ? "kernel.permitted" : "divider",
                    opacity: windowed ? 0.22 : 1,
                  }}
                />
              ))}
              {/* The data: when it actually did. */}
              {col.runs.map((r, i) => {
                const f = dayFraction(r.at, zone);
                if (f === null) return null;
                const late = outside.includes(r);
                return (
                  <Tooltip
                    key={`${r.at}-${i}`}
                    title={
                      `${new Date(r.at).toLocaleString()}`
                      + (r.usd ? ` — $${Number(r.usd).toFixed(4)}` : "")
                      + (late ? " — outside the window declared today" : "")
                      + (r.known ? "" : " — the only run older than the per-run records")
                    }
                  >
                    <Box
                      sx={{
                        position: "absolute",
                        left: `calc(${f * 100}% - 1px)`,
                        top: -2,
                        bottom: -2,
                        width: 3,
                        borderRadius: 0.5,
                        backgroundColor: late ? "kernel.attention" : "text.primary",
                        cursor: "help",
                      }}
                    />
                  </Tooltip>
                );
              })}
            </Box>
          </Stack>
        ))}
      </Stack>

      <Stack
        direction="row"
        spacing={2}
        sx={{ mt: 1, flexWrap: "wrap", rowGap: 0.5 }}
      >
        <Typography variant="caption" color="text.secondary">
          Midnight to midnight, {zone}
        </Typography>
        {windowed ? (
          <Typography variant="caption" color="text.secondary">
            Shaded is when it may start
          </Typography>
        ) : null}
      </Stack>

      {/* One line, and the qualifications go behind a control. Two
          paragraphs of caveat under a strip this small is more words than
          picture, which is the density the strip was meant to remove. */}
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: "block", mt: 1, maxWidth: "62ch" }}
      >
        {!runs.length
          ? "No run recorded in these days."
          : !windowed
            // Without a declared window everything is trivially inside one,
            // and saying so would reassure about a rule that does not exist.
            ? "No window declared — it may start at any hour."
            : outside.length
              ? `${outside.length} ${outside.length === 1 ? "run is" : "runs are"} outside the shaded hours.`
              : "Every recorded run is inside the shaded hours."}
      </Typography>

      <Box sx={{ mt: 0.5 }}>
        <Disclose label="What this does and does not say">
          <Stack spacing={1.5} sx={{ maxWidth: "62ch" }}>
            <Typography variant="body2" color="text.secondary">
              The shading is the window as declared <strong>today</strong>,
              drawn back across days when a different one — or none — may have
              been in force. A mark outside it means &ldquo;this would not be
              allowed now&rdquo;, which is not the same as &ldquo;it broke a
              rule&rdquo;.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Nothing marks a run that was expected and did not happen. That
              would mean inferring what a scheduler decided rather than reading
              what it did — only the declared window and the recorded runs are
              drawn.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Per-run records began when spending limits did, so anything older
              appears only if it is the most recent run the kernel kept.
            </Typography>
          </Stack>
        </Disclose>
      </Box>

    </Box>
  );
}
